import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ShieldCheck,
  FileCheck,
  ArrowRight,
  Server,
  Clock,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  RefreshCw,
  Trash2,
  CircleAlert
} from 'lucide-react';
// import ScanList from '../components/ScanList';
import GenerateReportModal from '../../components/Newdashboard/GenerateReportModal';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'

const LandingPage = () => {
  const navigate = useNavigate();
  const [availableScans, setAvailableScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // Fixed icon imports
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, scanId: null, scanName: null });
  const [deleting, setDeleting] = useState(false);
  const [systemStats, setSystemStats] = useState({
    activeFrameworks: 0,
    totalControls: 0
  });

  // Fetch available scans on component mount
  useEffect(() => {
    fetchAvailableScans();
  }, []);

  const fetchAvailableScans = async () => {
    try {
      setLoading(true);
      const response = await fetch(ApiNames.SCAN_REPORTS);


      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const scans = data.scan_reports || [];
      setAvailableScans(scans);

      // Calculate system stats from the latest scan data
      if (scans.length > 0) {
        // Use the most recent scan for system stats (first in array)
        const latestScan = scans[0];
        setSystemStats({
          activeFrameworks: latestScan.frameworks_count || 0,
          totalControls: latestScan.total_controls || 0
        });
      } else {
        // Reset to zeros if no scans available
        setSystemStats({
          activeFrameworks: 0,
          totalControls: 0
        });
      }
    } catch (error) {
      console.error('Error fetching scans:', error);
      toast.error('Failed to fetch available scans. Please ensure the API server is running.');
      setAvailableScans([]);
      // Reset stats on error
      setSystemStats({
        activeFrameworks: 0,
        totalControls: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAvailableScans();
    setRefreshing(false);
    toast.success('Scan list refreshed!');
  };

  const handleViewDashboard = (scanId) => {
    navigate(`/Dashboard/${scanId}`);
  };

  //   const handleGenerateReport = () => {
  //   navigate("/generatetest");
  // };
  const handleGenerateReport = () => {
    setShowGenerateModal(true);
  };

  const handleReportGenerated = (scanId) => {
    setShowGenerateModal(false);
    fetchAvailableScans(); // Refresh the list to show the new report
    toast.success('Report generated successfully! It now appears in the table below.');
  };

  const handleDeleteRequest = (scanId, scanName) => {
    setDeleteConfirm({ show: true, scanId, scanName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.scanId) return;

    try {
      setDeleting(true);
      const response = await fetch(ApiNames.REPORTS_SCAN_DELETE(deleteConfirm.scanId), {
        method: 'DELETE',
      });


      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      toast.success('Scan report deleted successfully!');

      // Refresh the scan list
      await fetchAvailableScans();

      // Close the confirmation dialog
      setDeleteConfirm({ show: false, scanId: null, scanName: null });
    } catch (error) {
      console.error('Error deleting scan:', error);
      toast.error('Failed to delete scan report. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, scanId: null, scanName: null });
  };

  const getStatusBadge = (status, complianceRate) => {
    // const badges = {
    //   good: 'badge-success',
    //   warning: 'badge-warning',
    //   danger: 'badge-danger'
    // };

    // const labels = {
    //   good: 'Excellent',
    //   warning: 'Needs Attention',
    //   danger: 'Critical Issues'
    // };

    const badges = {
      good: 'bg-success-100 text-success-600',
      warning: 'bg-warning-100 text-warning-600',
      danger: 'bg-red-100 text-red-600'
    };

    const labels = {
      good: 'Excellent',
      warning: 'Needs Attention',
      danger: 'Critical'
    };

    return (
      <span className={`inline-block text-xs font-semibold px-2 py-1 rounded-full ${badges[status]}` || 'badge-warning'}>
        {labels[status] || 'Unknown'} -
        {complianceRate || '0'}%
      </span>
    );
  };

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 backdrop-blur-sm border-b border-gray-200 sticky top-0 bg-[var(--main-ds-header)] shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)]">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 flex-col sm:flex-row gap-3">
            <div class="flex items-center space-x-3">
              <div class="flex items-center justify-center min-w-[2.5rem] w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-500 rounded-lg shadow-md">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 class="text-xl font-bold text-[var(--ds-heading)] m-0">Government Compliance Dashboard</h1>
                <p class="text-sm text-[var(--ds-text-500)] m-0">Federal Website Compliance Monitoring</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="btn-secondary flex"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              <button
                onClick={handleGenerateReport}
                className="btn-primary flex border text-nowrap"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                Generate Report
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      {/* <section className="relative py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h2 className="text-4xl font-bold sm:text-5xl lg:text-6xl text-[var(--ds-heading)]">
              Monitor Federal
              <span className="block text-primary-600">
                Compliance Standards
              </span>
            </h2>
            <p className="mt-6 text-xl max-w-3xl mx-auto text-[var(--ds-text-600)]">
              Comprehensive compliance monitoring across USWDS, OMB M-15-13,
              Section 508, and other federal standards. Generate detailed
              reports and track compliance trends for government websites.
            </p>

            <div className="mt-10 flex justify-center">
              <button
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
                onClick={handleGenerateReport}
              >
                <FileCheck className="w-6 h-6 mr-3" />
                Generate New Report
                <ArrowRight className="w-5 h-5 ml-3" />
              </button>
            </div>
          </div>
        </div>

        
        <div className="absolute top-20 left-20 w-20 h-20 bg-primary-100 rounded-full opacity-50 animate-pulse-slow"></div>
        <div
          className="absolute bottom-20 right-20 w-32 h-32 bg-indigo-100 rounded-full opacity-30 animate-pulse-slow"
          style={{ animationDelay: "1s" }}
        ></div>
      </section> */}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-500 text-white p-8 shadow-md mb-0 md:mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold mb-2">Monitor Federal <span className="text-yellow-300">Compliance Standards</span></h1>
            <p className="text-sm max-w-xl">
              Comprehensive compliance monitoring across USWDS, OMB M-15-13, Section 508, and other federal standards. Generate detailed reports and track compliance trends for government websites.
            </p>
          </div>
          <button
            className="flex items-center mt-0 bg-white text-blue-700 font-semibold px-3 md:px-5 py-3 rounded-lg shadow transition hover:bg-blue-100 hover:text-blue-700 text-nowrap"
            onClick={handleGenerateReport}
          >
            <FileCheck className="w-6 h-6 mr-3" /> Generate New Report
          </button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-7xl mx-auto py-5 px-4 sm:px-6 max-md:px-0 lg:px-8">
        <div className="">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card rounded-2xl">
              <div className="card-body text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-success-100 rounded-xl mb-4">
                  <CheckCircle className="w-6 h-6 text-success-600" />
                </div>
                <h3 className="text-3xl font-bold text-[var(--ds-heading)]">
                  {availableScans.length}
                </h3>
                <p className="text-[var(--ds-text-500)] text-sm">Available Scans</p>
              </div>
            </div>

            <div className="card rounded-2xl">
              <div className="card-body text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl mb-4">
                  <ShieldCheck className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-3xl font-bold text-[var(--ds-heading)]">
                  {systemStats.activeFrameworks}
                </h3>
                <p className="text-[var(--ds-text-500)] text-sm">Active Frameworks</p>
              </div>
            </div>

            <div className="card rounded-2xl">
              <div className="card-body text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-warning-100 rounded-xl mb-4">
                  <Server className="w-6 h-6 text-warning-600" />
                </div>
                <h3 className="text-3xl font-bold text-[var(--ds-heading)]">
                  {systemStats.totalControls}
                </h3>
                <p className=" text-[var(--ds-text-500)] text-sm">Total Controls</p>
              </div>
            </div>

            <div className="card rounded-2xl">
              <div className="card-body text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-xl mb-4">
                  <CircleAlert className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-3xl font-bold text-[var(--ds-heading)]">
                  35%
                </h3>
                <p className=" text-[var(--ds-text-500)] text-sm">Critical Issues</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Scans Section */}
      <section className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 shadow-[0px_0px_35px_0px_rgba(104,134,177,.15)] rounded-lg">
        <div className="">
          <div className="flex items-start sm:items-center justify-between mb-8 flex-col sm:flex-row sm:gap-3">
            <div>
              <h3 className="text-2xl font-bold text-[var(--ds-heading)]">
                Generated Compliance Reports
              </h3>
              <p className=" mt-2 text-[var(--ds-text-600)]">
                View detailed compliance dashboards for completed scans
              </p>
            </div>

            {availableScans.length > 0 && (
              <div className="text-sm text-[var(--ds-text-600)]">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            )}
          </div>

          {loading ? (
            <div className="">
              <div className="card-body">
                <div className="animate-pulse">
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-xl mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : availableScans.length === 0 ? (
            <div className="">
              <div className="card-body">
                <div className="text-center py-12">
                  <AlertTriangle className="mx-auto h-12 w-12 text-[--ds-text-400]" />
                  <h3 className="mt-4 text-lg font-medium text-[var(--ds-text-900)]">
                    No compliance reports generated
                  </h3>
                  <p className="mt-2 text-[--ds-text-500]">
                    Generate your first compliance report to see it listed here.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleGenerateReport}
                      className="btn-primary"
                    >
                      <FileCheck className="w-4 h-4 mr-2" />
                      Generate First Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="">
              <div className="card-header">
                <h4 className="text-lg font-medium text-[var(--ds-text-900)]">
                  Scan Reports
                </h4>
              </div>
              <div className="card-body p-0">
                <div className="overflow-hidden overflow-x-auto">
                  <table className="min-w-full ">
                    <thead className="bg-[var(--ds-gray-100-bg)]">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ds-text-500)] uppercase tracking-wider">
                          URL Information
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ds-text-500)] uppercase tracking-wider">
                          Scan Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ds-text-500)] uppercase tracking-wider">
                          Compliance Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ds-text-500)] uppercase tracking-wider">
                          Last Updated
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-[var(--ds-text-500)] uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-[var(--ds-body)] ">
                      {availableScans.map(scan => (
                        <tr
                          key={scan.scan_id}
                          className="hover:bg-[var(--ds-body-hover)] transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-[.8125rem] font-medium text-[var(--ds-text-900)]">
                                <Link className='text-blue-600' target='_blank' to={scan.url_name}>{scan.url_name}</Link>
                              </div>
                              <div className="text-[.8125rem] text-[var(--ds-text-500)]">
                                URL ID: {scan.url_id}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-[.8125rem] font-medium text-[var(--ds-text-900)]">
                                {scan.scan_id.substring(0, 12)}...
                              </div>
                              <div className="text-[.8125rem] text-[var(--ds-text-500)]">
                                {scan.frameworks_count} frameworks •{" "}
                                {scan.total_controls} controls
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getStatusBadge(scan.status, scan.compliance_rate)}
                              {/* <span className="text-[.8125rem] font-medium text-[var(--ds-text-900)]">
                                {scan.compliance_rate}%
                              </span> */}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[.8125rem] text-[var(--ds-text-500)]">
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2" />
                              {new Date(scan.generated_at).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-[.8125rem] font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() =>
                                  handleViewDashboard(scan.scan_id)
                                }
                                className="inline-flex items-center px-4 py-2 border border-transparent text-[.8125rem] font-medium rounded-lg text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors duration-200"
                              >
                                View Dashboard
                                {/* <ArrowRight className="w-4 h-4 ml-2" /> */}
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteRequest(
                                    scan.scan_id,
                                    scan.url_name,
                                  )
                                }
                                className="inline-flex items-center px-3 py-2 border border-transparent text-[.8125rem] font-medium rounded-lg text-red-700 bg-red-100 hover:bg-red-200 transition-colors duration-200"
                                title="Delete scan report"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      {/* Generate Report Modal */}
      <GenerateReportModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onReportGenerated={handleReportGenerated}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>

            <h3 className="text-lg font-medium text-[--ds-text-900] text-center mb-2">
              Delete Scan Report
            </h3>

            <p className="text-sm text-[var(--ds-text-500)] text-center mb-6">
              Are you sure you want to delete the scan report for{" "}
              <span className="font-medium text-[--ds-text-900]">
                {deleteConfirm.scanName}
              </span>
              ? This action cannot be undone and will remove all associated
              data.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
};

export default LandingPage;